import type {Messages} from './en';

const de: Messages = {
    languageSwitcher: {
        label: 'Sprache',
    },
    nav: {
        tagline: 'Design-Toolkit für Embedded-GUIs',
        logoAlt: 'Lopaka-Logo',
        gallery: 'Galerie',
        journal: 'Blog',
        docs: 'Dokumentation',
        contactUs: 'Kontakt',
        sourceCode: 'Quellcode',
        followTwitter: 'Folge uns auf Twitter',
        followInstagram: 'Folge uns auf Instagram',
        joinDiscord: 'Tritt dem Lopaka Hub auf Discord bei',
        signIn: 'Anmelden',
        projects: 'Projekte',
    },
    legal: {
        cookies: 'Wir verwenden Cookies. {terms}, {privacy}',
        agreement: 'Wenn du fortfährst, stimmst du den {terms} und der {privacy} von Lopaka zu',
        terms: 'Nutzungsbedingungen',
        privacy: 'Datenschutzerklärung',
    },
    meta: {
        home: {
            title: 'Grafikeditor und Bildkonverter für Arduino, ESP32, Adafruit_GFX, u8g2, TFT_eSPI',
            description:
                'UI-Grafikeditor für Embedded-Displays mit pixelgenauem Code-Generator und Export. Ideal für Arduino, STM32 und ESP32. Wandelt Bilder in uint32- und uint8-Variablen für Adafruit GFX, u8g2 und TFT_eSPI um',
        },
        gallery: {
            title: 'Galerie mit UI-Projekten für Embedded-Displays — Lopaka',
            description:
                'Entdecke UI-Designs der Community für OLED-, TFT- und E-Paper-Displays. Öffne jedes Projekt im Lopaka-Editor und erzeuge daraus Code für Arduino, ESP32 oder STM32.',
        },
        price: {
            title: 'Preise und Tarife — Lopaka',
            description:
                'Vergleiche den kostenlosen Zugang zu Lopaka mit Plus Monthly und Plus Lifetime. Schalte eigene Schriftarten, Icon-Pakete, Animationen, private Projekte und unbegrenzt viele Screens frei.',
        },
        app: {
            description:
                'Browserbasierter Grafikeditor und Bildkonverter für Embedded-Displays mit Arduino, ESP32 und STM32. Unterstützt die Bibliotheken Adafruit_GFX, u8g2 und TFT_eSPI.',
        },
        appPages: {
            projects: 'Deine Projekte — Lopaka',
            assets: 'Deine Assets — Lopaka',
            login: 'Anmelden — Lopaka',
            loginExpired: 'Anmeldelink abgelaufen — Lopaka',
            upgrade: 'Tarif upgraden — Lopaka',
            success: 'Zahlung erhalten — Lopaka',
        },
    },
    home: {
        hero: {
            headingLead: 'GUI-Design-Toolkit für',
            headingAccent: 'Embedded-Displays',
            taglineLead: 'Bring deine Grafiken in Minuten aufs Display,',
            taglineAccent: 'nicht in Tagen',
            taglineTail: 'mit visuellem Editor + Code-Generator für Mikrocontroller',
            getStarted: 'Jetzt starten',
        },
        quickStart: {
            heading: 'Bring deinen ersten Screen zum Laufen',
            intro: 'Starte mit einem echten Display-Layout, passe es visuell an und exportiere Code, den du direkt in dein Projekt übernehmen kannst.',
            steps: {
                pick: 'Wähle ein Display und eine GFX-Bibliothek',
                edit: 'Ändere Texte, Icons oder das Layout',
                export: 'Exportiere den Code und bring ihn auf deine Hardware',
            },
            getStarted: 'Jetzt starten',
            browseTemplates: 'Vorlagen ansehen',
            playVideo: 'Schnellstart-Video von Lopaka abspielen',
            videoTitle: 'Schnellstart-Video von Lopaka',
            watchVideo: 'Im Video siehst du, wie es funktioniert',
        },
        features: {
            heading: 'Embedded-GUIs leicht gemacht',
            tagline: 'Ein komplettes Toolset für Grafiken, die auf Mikrocontrollern laufen.',
            shipFaster: {
                title: 'Hardware schneller fertigstellen',
                text: 'Erzeuge automatisch produktionsreifen Code für jeden Mikrocontroller, ganz ohne Handarbeit. Konzentrier dich aufs Design statt auf einzelne Pixel.',
            },
            confidence: {
                title: 'Mit Sicherheit gestalten',
                text: 'Optimierte Grafiken, die auch auf Hardware mit niedriger Auflösung scharf und klar aussehen. Was du siehst, erscheint genau so auf deinem Gerät.',
                imageAlt: 'Pixelgenaue Display-Vorschau',
            },
            style: {
                title: 'Mit Stil gestalten',
                text: 'Hunderte optimierte Icons, die deine Nutzer leiten. Von Hand gestaltet für kleine Displays.',
                imageAlt: 'Icon-Bibliothek für Embedded-Displays',
            },
            readability: {
                title: 'Bessere Lesbarkeit',
                text: 'Greif auf eine Bibliothek pixelgenauer Schriftarten zu, die auf Embedded-Displays klar lesbar sind. Importiere TTF-, OTF-, BDF- und GFX-Schriftarten.',
            },
            platforms: {
                title: 'Jede Plattform nutzen',
                text: '{platforms} und mehr. Ein Tool für alle deine Embedded-Projekte.',
            },
            projects: {
                title: 'Komplexe Projekte verwalten',
                text: 'Screens mit mehreren Ebenen und mehrere Displays in einem Projekt. Organisiere deine UI ganz einfach.',
                imageAlt: 'Projektverwaltung mit mehreren Ebenen',
            },
            workflow: {
                title: 'In deinen Workflow integrieren',
                text: 'Exportiere Assets genau in dem Format, das dein Projekt braucht – von C++ bis zu rohen Bitmaps. PNG-, BMP- und XBMP-Arrays, bereit für den Embedded-Einsatz.',
            },
            brand: {
                title: 'Einheitlicher Markenauftritt',
                text: 'Lade die Schriftarten deiner Marke und eigene Assets hoch.',
                result: 'Sieh, wie sie auf deinem Gerät perfekt dargestellt werden.',
            },
        },
        templates: {
            heading: 'Hunderte Vorlagen',
            tagline: 'Von der Community erstellt. Von unserem Team ausgewählt.',
            exploreMore: 'Mehr entdecken',
        },
        plansHeading: 'Fang noch heute an',
        community: {
            contribute: {
                title: 'Mitmachen',
                text: 'Hilf uns, die Zukunft von Embedded-UIs zu gestalten. Open Source und von der Community getragen.',
                action: 'Auf GitHub ansehen',
            },
            discord: {
                title: 'Tritt dem Discord Hub bei',
                text: 'Vernetze dich mit anderen Entwicklern, teile deine Projekte und hol dir Hilfe.',
                action: 'Server beitreten',
            },
            follow: {
                title: 'Folge uns',
                text: 'Bleib auf dem Laufenden über Neuigkeiten, Funktionen und Projekte aus der Community.',
            },
        },
        rating: {
            label: 'Nutzerbewertung',
            stars: '{rating} von 5 Sternen',
            summary: 'Durchschnittliche Bewertung aus {count} Antworten unserer Nutzerumfrage',
        },
    },
    socialProof: {
        heading: 'Die Kommentare sprechen für sich',
        label: 'Instagram-Kommentare',
        avatarAlt: 'Avatar von {username}',
    },
    mobileIntro: {
        heading: 'Am Computer weitermachen',
        text: 'Lopaka ist als Desktop-Anwendung gedacht, damit du die beste Erfahrung hast.',
        close: 'Hinweis zum Weitermachen am Computer schließen',
        send: 'Schick mir einen Link',
        sending: 'Link wird gesendet',
        sent: 'Wir haben dir einen Link für den Computer per E-Mail geschickt.',
        failed: 'Der Link konnte nicht gesendet werden. Bitte versuch es noch einmal.',
        watchGuide: 'Schnellstart-Anleitung ansehen',
    },
    gallery: {
        tabs: {
            highlights: {title: 'Highlights', description: 'Vom Lopaka-Team ausgewählt'},
            trending: {title: 'Im Trend', description: 'Die meisten Sterne und Forks'},
            favourite: {title: 'Favoriten', description: 'Deine Sterne'},
            all: {title: 'Alles', description: 'Alle öffentlichen Projekte'},
        },
        clearFilters: 'Filter zurücksetzen',
        empty: 'Keine Projekte in der Galerie gefunden.',
        showMore: 'Mehr anzeigen',
        project: {
            screens: 'Screens: {count}',
            starThis: 'Mit Stern markieren',
            stars: 'Sterne: {count}',
            forks: 'Forks: {count}',
        },
    },
    filters: {
        width: 'Breite',
        height: 'Höhe',
        all: 'Alle',
        allPlatforms: 'Alle Plattformen',
        search: 'Suchen',
        clearSearch: 'Filter nach Projektname löschen',
    },
    pricing: {
        features: {
            codeGeneration: 'Code-Generierung',
            imageProcessing: 'Bildverarbeitung',
            communityFonts: 'Community-Schriftarten',
            communityIcons: 'Community-Icons',
            screens: '{count} Screens',
            assetsStorage: 'Speicher für {count} Assets',
            customFonts: 'Eigene Schriftarten',
            extraIcons: 'Über 1500 zusätzliche Icons',
            publicProjects: '{count} öffentliche Projekte',
            screensPerProject: '{count} Screens pro Projekt',
            animations: 'Animationen',
            exportOptions: 'Exportoptionen',
            everythingInBasic: 'Alles aus Basic, dazu:',
            unlimitedScreens: 'Unbegrenzte Screens',
            unlimitedAssets: 'Unbegrenzte Assets',
            privacySettings: 'Privatsphäre-Einstellungen',
            presentationMode: 'Präsentationsmodus',
            teamCollaboration: 'Zusammenarbeit im Team',
            customPlatformSupport: 'Unterstützung eigener Plattformen',
            apiAccess: 'API-Zugriff & Integrationen',
            customFeatures: 'Individuelle Funktionen',
            userManagement: 'SSO & Benutzerverwaltung',
            onPremise: 'On-Premise-Betrieb',
            whiteLabel: 'White-Label-Optionen',
            volumeLicensing: 'Volumenlizenzen',
            dailyBackups: 'Tägliche Backups',
            onboarding: 'Schulung & Onboarding',
            dedicatedSupport: 'Persönlicher Support',
        },
        upgradeHeading: 'Wechsle deinen Tarif, um die Funktionen von {plan} freizuschalten:',
        teamsDescription: 'Maßgeschneiderte Lösungen für Hardware-Unternehmen, IoT-Firmen und Entwicklungsteams',
        card: {
            free: 'KOSTENLOS',
            save: '{amount} sparen',
            comparedToMonthly: 'Im Vergleich zur monatlichen Zahlung',
            perYear: '/ Jahr',
            perMonth: '/ Monat',
            monthlyEquivalent: '~ {price}/Monat',
            sevenDayPass: '7-Tage-Pass',
            lifetimeAccess: 'Lebenslanger Zugang',
            oneTimePayment: 'Einmalzahlung',
            everythingInPlus: 'Alles aus Plus, dazu:',
            currentPlan: 'Aktueller Tarif',
            signUp: 'Registrieren',
            requestQuote: 'Angebot anfordern',
        },
        actions: {
            buyNow: 'Jetzt kaufen',
            getWeekPass: '7-Tage-Zugang holen',
            requestLifetime: 'Lifetime anfragen',
            getLifetime: 'Lebenslange Lizenz holen',
            changeToAnnual: 'Zu jährlich wechseln',
            changeToMonthly: 'Zu monatlich wechseln',
            changeToPlus: 'Zu Plus wechseln',
            upgradeToPlus: 'Auf Plus upgraden',
        },
        referral: {
            extraDays: '+ Du bekommst 7 zusätzliche Tage',
            discount: '+ Du bekommst 10 % Rabatt',
        },
        education: {
            heading: 'Lopaka für den Unterricht',
            question: 'Bist du Lehrkraft und möchtest Lopaka Plus im Unterricht einsetzen?',
            offer: 'Lehrkräfte, die Lopaka im Unterricht nutzen möchten, bekommen von uns {access}.',
            access: 'ein Jahr lang kostenlosen Plus-Zugang',
            contact: 'Kontakt aufnehmen',
        },
    },
    auth: {
        signInTab: 'Anmelden',
        registerTab: 'Neues Konto erstellen',
        or: 'ODER',
        continueWithGithub: 'Weiter mit GitHub',
        continueWithGoogle: 'Weiter mit Google',
        signOut: 'Abmelden',
        email: 'E-Mail',
        password: 'Passwort',
        passwordPlaceholder: 'Gib dein Passwort ein',
        newPassword: 'Neues Passwort',
        newPasswordPlaceholder: 'Neues Passwort festlegen',
        confirmPassword: 'Passwort bestätigen',
        confirmPasswordPlaceholder: 'Neues Passwort wiederholen',
        forgotPassword: 'Passwort vergessen?',
        backToSignIn: 'Zurück zur Anmeldung',
        resetSent:
            'Öffne die E-Mail, die wir an {email} geschickt haben, und folge dem Link, um ein neues Passwort festzulegen.',
        registerSent: 'Wir haben einen Bestätigungslink an {email} geschickt.',
        registerSentNext: 'Öffne die E-Mail, um dein Konto zu bestätigen und die Anmeldung abzuschließen.',
        modes: {
            signIn: {title: 'Bei deinem Konto anmelden', submit: 'Anmelden'},
            register: {title: 'Konto erstellen', submit: 'Konto erstellen'},
            forgotPassword: {title: 'Passwort zurücksetzen', submit: 'Link zum Zurücksetzen anfordern'},
            resetPassword: {title: 'Neues Passwort festlegen', submit: 'Passwort ändern'},
        },
        messages: {
            signingIn: 'Du wirst angemeldet.',
            resetLinkSent: 'Wir haben einen Link zum Zurücksetzen des Passworts an {email} geschickt.',
            passwordUpdated: 'Dein Passwort wurde geändert.',
        },
        validation: {
            signInMissing: 'Gib deine E-Mail-Adresse und dein Passwort ein.',
            registerMissing: 'Gib deine E-Mail-Adresse ein und lege ein Passwort fest.',
            accountExists: 'Es gibt bereits ein Konto mit dieser E-Mail-Adresse. Bitte melde dich an.',
            resetEmailMissing: 'Gib deine E-Mail-Adresse ein, um das Passwort zurückzusetzen.',
            newPasswordMissing: 'Gib ein neues Passwort ein.',
            passwordsDiffer: 'Die Passwörter müssen übereinstimmen.',
        },
        errors: {
            invalid_credentials: 'E-Mail-Adresse oder Passwort ist falsch.',
            email_not_confirmed: 'Bestätige zuerst deine E-Mail-Adresse: Öffne den Link, den wir dir geschickt haben.',
            user_already_exists: 'Es gibt bereits ein Konto mit dieser E-Mail-Adresse. Bitte melde dich an.',
            email_exists: 'Es gibt bereits ein Konto mit dieser E-Mail-Adresse. Bitte melde dich an.',
            weak_password: 'Dieses Passwort ist zu schwach. Wähle ein längeres Passwort.',
            same_password: 'Das neue Passwort muss sich vom aktuellen unterscheiden.',
            email_address_invalid: 'Gib eine gültige E-Mail-Adresse ein.',
            over_email_send_rate_limit:
                'Es wurden zu viele E-Mails gesendet. Bitte warte ein paar Minuten und versuch es dann noch einmal.',
            over_request_rate_limit: 'Zu viele Versuche. Bitte warte ein paar Minuten und versuch es dann noch einmal.',
        },
        expired: {
            title: 'Link zum Zurücksetzen abgelaufen',
            text: 'Dieser Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen. Fordere eine neue E-Mail an und versuch es noch einmal.',
            backToProjects: 'Zurück zur Projektliste',
        },
        emailPlaceholder: 'du{at}beispiel.de',
    },
    notFound: {
        title: 'Seite nicht gefunden',
        text: 'Die gesuchte Seite wurde nicht gefunden. Vielleicht wurde sie gelöscht oder auf privat gestellt, oder die Adresse ist falsch.',
        toGallery: 'Zur Galerie',
        toProjects: 'Meine Projekte',
    },
    appUpdate: {
        available: 'Eine neue Lopaka-Version ist verfügbar!',
        update: 'Aktualisieren',
        loadFailed: 'Seite konnte nicht geladen werden',
        loadFailedText:
            'Die Seite konnte nicht geladen werden. Das kann an einem Netzwerkproblem, einem Werbeblocker oder einem kürzlichen App-Update liegen.',
        reload: 'Seite neu laden',
    },
    pageShell: {
        home: {
            exportTargets:
                'Gestalte einen Screen im Browser und exportiere kompilierfertigen Quellcode für {libraries}.',
            imageImport:
                'Importiere PNG, BMP, GIF oder Video, und Lopaka wandelt jedes Frame in das Format um, das dein Display braucht – von 1-Bit-XBMP-Bitmaps für monochrome OLEDs bis zu RGB565-Arrays für Farb-TFTs.',
            gallery: 'Stöbere in der {galleryLink}, öffne jedes Design im Editor und erzeuge daraus Code.',
            galleryLink: 'Projektgalerie',
            more: 'Lies die {docsLink} oder vergleiche {pricingLink}.',
            docsLink: 'Dokumentation',
            pricingLink: 'Tarife und Preise',
        },
        gallery: {
            heading: 'Galerie mit UI-Projekten für Embedded-Displays',
            intro: 'UI-Designs der Community für OLED-, TFT- und E-Paper-Displays. Öffne jedes Projekt im Lopaka-Editor und erzeuge daraus Code für Arduino, ESP32 oder STM32.',
            more: 'Starte dein eigenes Design im {editorLink} oder lies die {docsLink}.',
            editorLink: 'Lopaka-Editor',
            docsLink: 'Dokumentation',
        },
        price: {
            heading: 'Preise und Tarife',
            intro: 'Vergleiche die Tarife {plans} des Lopaka-Editors für Embedded-GUIs, mit monatlicher oder jährlicher Abrechnung.',
            plans: {
                basic: 'Kostenlos nutzbar.',
                hobby: 'Für regelmäßige private Projekte.',
                plus: 'Für Arbeit ohne Limits.',
                teams: 'Maßgeschneiderte Lösungen für Hardware-Unternehmen, IoT-Firmen und Entwicklungsteams.',
            },
            includes: 'Enthält {features}.',
            teachers:
                'Lehrkräfte können ein Jahr lang kostenlosen Plus-Zugang beantragen. Details zu den Tarifen findest du in der {docsLink}.',
            docsLink: 'Tarif-Dokumentation',
        },
    },
    common: {
        cancel: 'Abbrechen',
        save: 'Speichern',
        delete: 'Löschen',
        close: 'Schließen',
        back: 'Zurück',
        next: 'Weiter',
        previous: 'Zurück',
        finish: 'Fertigstellen',
        done: 'Fertig',
        apply: 'Anwenden',
        confirm: 'Bestätigen',
        areYouSure: 'Bist du sicher?',
        edit: 'Bearbeiten',
        rename: 'Umbenennen',
        duplicate: 'Duplizieren',
        remove: 'Entfernen',
        add: 'Hinzufügen',
        copy: 'Kopieren',
        copied: 'Kopiert',
        download: 'Herunterladen',
        upload: 'Hochladen',
        search: 'Suchen',
        reset: 'Zurücksetzen',
        retry: 'Erneut versuchen',
        loading: 'Wird geladen …',
        saving: 'Wird gespeichert …',
        upgrade: 'Upgraden',
        upgradeNow: 'Jetzt upgraden',
        public: 'Öffentlich',
        private: 'Privat',
        configure: 'Konfigurieren',
        projectSettings: 'Projekteinstellungen',
        selectAll: 'Alle auswählen',
        deselectAll: 'Auswahl aufheben',
        none: 'Keine',
        yes: 'Ja',
        no: 'Nein',
        learnMore: 'Mehr erfahren',
        unknownError: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    },
    errors: {
        unauthenticated: 'Melde dich an, um deine Projekte zu verwalten.',
        ownership_denied: 'Dieses Projekt oder dieser Screen gehört zu einem anderen Konto.',
        not_found: 'Projekt oder Screen nicht gefunden. Möglicherweise wurde es gelöscht.',
        limit_reached: 'Du hast das Limit deines Tarifs erreicht. Upgrade, um mehr hinzuzufügen.',
        invalid_input: 'Diese Aktion ist hier nicht möglich.',
        read_failed: 'Deine Daten konnten nicht geladen werden. Prüfe deine Verbindung und versuche es erneut.',
        write_failed:
            'Deine Änderungen konnten nicht gespeichert werden. Prüfe deine Verbindung und versuche es erneut.',
        screenLimit: 'Du hast das Screen-Limit deines Tarifs erreicht. Upgrade, um weitere Screens hinzuzufügen.',
        animationTooLarge:
            'Die Animation ist größer als 5 MiB. Verringere die Anzahl der Frames oder die Größe der Zeichenfläche, bevor du speicherst.',
        animationUploadUnknown:
            'Wir konnten nicht bestätigen, dass die Animation gespeichert wurde. Prüfe deine Verbindung und speichere erneut.',
        animationLastFrame: 'Eine Animation braucht mindestens ein Frame.',
        assetLimit: 'Upload-Limit für Assets erreicht. Upgrade deinen Tarif, um mehr hochzuladen.',
        signInToImportImages: 'Melde dich an, um Bilder zu importieren.',
        signInToSaveAnimations: 'Melde dich an, um Animationen zu speichern.',
        screenCodeLoading: 'Warte, bis der Screen-Code geladen ist.',
        codeEditorClosed: 'Der Code-Editor ist geschlossen.',
        readonly: 'Dieser Screen ist schreibgeschützt.',
        stale_context: 'Der Screen hat sich geändert, bevor die Aktion abgeschlossen war. Versuche es erneut.',
        unsupported_for_platform: 'Die aktuelle Plattform unterstützt das nicht.',
        upgrade_required: 'Upgrade deinen Tarif, um das zu nutzen.',
        importOneVideo: 'Wähle jeweils nur eine Videodatei aus.',
        importVideoUnavailable: 'Der Import von Video-Animationen ist in diesem Editor nicht verfügbar.',
        importVideoUnreadable: 'Der Container des ausgewählten Videos konnte nicht gelesen werden.',
        importVideoNoTrack: 'Die ausgewählte Datei enthält keine Videospur.',
        importVideoCodec: 'Dieser Browser kann den Video-Codec der ausgewählten Datei nicht dekodieren.',
        importVideoInvalid: 'Das ausgewählte Video hat eine ungültige Dauer oder ungültige Abmessungen.',
        importVideoNoPreview: 'Es konnten keine Vorschau-Frames des Videos dekodiert werden.',
        importGifNoFrames: 'Wähle mindestens ein GIF-Frame zum Importieren aus.',
        importGifFrameSize: 'Alle GIF-Frames müssen nach der Verarbeitung dieselben Abmessungen haben.',
        importGifTooLarge: 'Das verarbeitete GIF überschreitet das Größen- oder Frame-Limit für Animationen.',
    },
    projects: {
        heading: 'Meine Projekte',
        newProject: 'Neues Projekt',
        createHint: 'Neues Projekt erstellen',
        upgradeHint: 'Upgrade, um weitere Projekte hinzuzufügen',
        projectLimit: {
            title: 'Du hast das Projektlimit deines aktuellen Tarifs erreicht.',
            text: 'Upgrade jetzt, um mehr Projekte und erweiterte Funktionen freizuschalten.',
        },
        screenLimit: {
            title: 'Du hast das Screen-Limit deines aktuellen Tarifs erreicht.',
            text: 'Upgrade jetzt, um mehr Screens und erweiterte Funktionen freizuschalten.',
        },
        noFilterResults: 'Keine Projekte für die gewählten Filter gefunden.',
        resetFilters: 'Filter zurücksetzen',
        empty: 'Erstelle ein {newProject} oder entdecke Vorlagen in der {gallery}',
        emptyNewProject: 'Neues Projekt',
        emptyGallery: 'Galerie',
        community: {
            eyebrow: 'Community',
            heading: 'Projektgalerie',
            exploreMore: 'Mehr entdecken',
        },
        favouritesOnly: 'Nur Favoriten anzeigen',
        card: {
            copy: 'Dieses Projekt kopieren',
            screens: 'Screens: {count}',
        },
        title: {
            publicHint: 'Dieses Projekt ist für alle Nutzer in der Galerie sichtbar',
            publicProject: 'Öffentliches Projekt',
            deleteHint: 'Projekt endgültig löschen',
        },
        examples: {
            eyebrow: 'Schnellstart',
            heading: 'Beispielprojekte',
            open: 'Im Editor öffnen',
            videoHeading: 'Video-Tutorial',
            videoTitle: 'YouTube-Videoplayer',
            colorful: 'Farbe',
            monochrome: 'monochrom',
            speedometer:
                'Fahrzeug-Dashboard mit Geschwindigkeit, Drehzahl, Kilometerzähler, Spannung, Tankstand und Zündstatus',
            tirePressure: 'Live-Werte aller vier Räder auf einer Draufsicht des Autos',
        },
        wizard: {
            editTitle: 'Projekt bearbeiten',
            createTitle: 'Neues Projekt erstellen',
            platform: 'Plattform',
            title: 'Titel',
            titlePlaceholder: 'Projekttitel eingeben',
            screenSize: 'Screen-Größe',
            swap: 'Abmessungen tauschen',
            selectSize: 'Screen-Größe wählen',
            custom: 'Benutzerdefiniert',
            rounded: 'Rund',
            roundedHint: 'Rundes Display: Breite = Höhe, kreisförmige Maske auf der Zeichenfläche',
            background: 'Hintergrundfarbe',
            backgroundHex: 'Hintergrundfarbe als Hex-Wert',
            publish: 'In der Galerie veröffentlichen',
            publicHint: 'Öffentliche Projekte sind für alle Nutzer sichtbar',
            upgradeHint: 'Unbegrenzte Screens und Datenschutzeinstellungen erhalten',
            deleteHint:
                'Dieses Projekt und seine Screens endgültig entfernen. Das kann nicht rückgängig gemacht werden.',
            createPublic: 'Öffentliches Projekt erstellen',
            createFailed: 'Das Projekt konnte nicht erstellt werden.',
            saveFailed: 'Das Projekt konnte nicht gespeichert werden.',
            deleteFailed: 'Das Projekt konnte nicht gelöscht werden.',
            defaultTitle: 'Projekt {number}',
        },
        copyFailed: 'Das Projekt konnte nicht kopiert werden.',
    },
    animationEditor: {
        topBar: {
            frameCounter: 'Frame {current} / {total}',
            cancelHint: 'Alle nicht gespeicherten Änderungen an der Animation gehen verloren.',
        },
        timeline: {
            deleteFrame: 'Ausgewähltes Frame löschen',
            firstFrame: 'Zum ersten Frame springen',
            previousFrame: 'Vorheriges Frame',
            play: 'Animation abspielen',
            pause: 'Animation pausieren',
            nextFrame: 'Nächstes Frame',
            lastFrame: 'Zum letzten Frame springen',
            duplicateFrame: 'Frame duplizieren',
            addFrame: 'Frame hinzufügen',
            animationColor: 'Animationsfarbe',
            totalLength: 'Gesamtlänge',
            durationSeconds: '{seconds} s',
            fpsHint: 'Frames pro Sekunde, von 1 bis 60',
            fps: 'Frames pro Sekunde',
            fpsValue: '{fps} FPS',
            resizeCanvas: 'Größe der Zeichenfläche ändern',
            estimatedSize: 'Geschätzte Größe',
            onionSkinHint: 'Vorheriges Frame anzeigen',
            onionSkin: 'Zwiebelschicht',
            onion: 'Zwiebel',
            frame: 'Frame {number}',
            dropCopy: '+ Kopie',
            dropMove: 'Verschieben',
        },
        resizeDialog: {
            title: 'Größe der Animations-Zeichenfläche ändern',
            description: 'Die Pixel bleiben oben links verankert und werden nicht skaliert.',
            width: 'Breite',
            height: 'Höhe',
            resize: 'Größe ändern',
            close: 'Dialog zum Ändern der Größe schließen',
        },
        errors: {
            previewFailed: 'Die Animationsvorschau ist fehlgeschlagen.',
            frameLoadFailed: 'Das Frame konnte nicht geladen werden.',
            operationFailed: 'Die Aktion an der Animation ist fehlgeschlagen.',
            playbackFailed: 'Die Wiedergabe der Animation konnte nicht gestartet werden.',
            flattenFailed: 'Das aktuelle Frame konnte nicht auf eine Ebene reduziert werden.',
            imageAddFailed: 'Das Bild konnte nicht hinzugefügt werden.',
            animationLoadFailed: 'Die Animation konnte nicht geladen werden.',
            animationAddFailed: 'Die Animation konnte nicht hinzugefügt werden.',
            assetUnavailable:
                'Das Animations-Asset ist nicht verfügbar. Stattdessen wurde eine leere bearbeitbare Animation geöffnet.',
        },
    },
    inspector: {
        selectedLayers: 'Ausgewählte Ebenen ({count})',
        labels: {
            font: 'Schriftart',
            fontSize: 'Größe',
            inverted: 'Invertiert (XOR)',
            fill: 'Gefüllt',
            color: 'Füllfarbe',
            overlay: 'Overlay',
            alphaChannel: 'Transparenz',
            radius: 'R',
            rx: 'RX',
            ry: 'RY',
            w: 'B',
            h: 'H',
            backgroundColor: 'Hintergrund',
            borderColor: 'Rahmen',
            borderWidth: 'RB',
            checked: 'Aktiviert',
            rotation: 'Drehung',
            startAngle: 'Start',
            endAngle: 'Ende',
            value: 'Wert',
            width: 'Breite',
            smooth: 'Glatt',
        },
        labelHints: {
            radius: 'Radius',
            rx: 'Horizontaler Radius',
            ry: 'Vertikaler Radius',
            w: 'Breite',
            h: 'Höhe',
            backgroundColor: 'Hintergrundfarbe',
            borderColor: 'Rahmenfarbe',
            borderWidth: 'Rahmenbreite',
            rotation: 'Drehung',
            startAngle: 'Startwinkel',
            endAngle: 'Endwinkel',
        },
        tooltips: {
            alphaChannel: 'Transparente Pixel bleiben durchsichtig. Aus: Sie werden mit dem Screen-Hintergrund gefüllt',
            overlay: 'Ebene abdunkeln und von der Code-Generierung ausnehmen',
        },
        textPlaceholder: 'Text eingeben …',
        hexColor: 'Hex-Farbe für {label}',
        addVariable: 'Variable hinzufügen',
        removeVariable: 'Variable entfernen',
        rotateText: 'Text um {degrees}° drehen',
        mergeToBitmap: 'Zu Bitmap zusammenführen',
        mergeHint: 'Ebenen zu einer einzigen Bitmap zusammenführen',
        editAnimationHint: 'Frames der Animation bearbeiten',
        mergeFailed: 'Die Ebenen konnten nicht zusammengeführt werden.',
        mergeTooLarge: 'Das zusammengeführte Bild ist zu groß. Rücke die ausgewählten Ebenen näher zusammen.',
        mergeNothingSelected: 'Wähle Ebenen zum Zusammenführen aus.',
        fontLoadFailed:
            'Die Schriftart konnte nicht geladen werden. Prüfe, ob sie dem GFX- oder BDF-Format entspricht.',
        align: {
            left: 'Links ausrichten',
            centerH: 'Horizontal zentrieren',
            right: 'Rechts ausrichten',
            top: 'Oben ausrichten',
            centerV: 'Vertikal zentrieren',
            bottom: 'Unten ausrichten',
        },
        imageActions: {
            flipH: 'Horizontal spiegeln',
            flipV: 'Vertikal spiegeln',
            rotate: 'Im Uhrzeigersinn drehen',
            invert: 'Farben invertieren',
            trimPadding: 'Leere Ränder entfernen',
            download: 'Bild herunterladen',
            saveAsset: 'Zu den Projekt-Assets hinzufügen',
            saveAssetLabel: 'Als Asset speichern',
            imageSaved: 'Das Bild wurde in den Projekt-Assets gespeichert.',
        },
        colorInput: {
            noBackground: 'Kein Hintergrund',
            clearBackground: 'Hintergrund entfernen',
        },
        colors: {
            red: 'Rot',
            pink: 'Pink',
            purple: 'Lila',
            deepPurple: 'Dunkellila',
            indigo: 'Indigo',
            blue: 'Blau',
            lightBlue: 'Hellblau',
            cyan: 'Cyan',
            teal: 'Blaugrün',
            green: 'Grün',
            lightGreen: 'Hellgrün',
            lime: 'Limette',
            yellow: 'Gelb',
            amber: 'Bernstein',
            orange: 'Orange',
            deepOrange: 'Dunkelorange',
            brown: 'Braun',
            blueGrey: 'Blaugrau',
        },
    },
    paint: {
        selectBrushColor: 'Pinselfarbe {color} auswählen',
        paintColor: 'Malfarbe',
        brushSize: 'Pinselgröße',
        mono: 'Mono',
        cropImage: 'Bild zuschneiden',
        applyCropHint: 'Zuschnitt anwenden (Enter)',
        cancelCropHint: 'Zuschnitt abbrechen (ESC)',
        eraser: 'Radierer',
        fill: 'Füllen',
        patternHint: 'Pinselform und Muster',
        sizeLimit:
            'Diese Malebene hat ihre maximale Größe von {pixels} Pixeln erreicht. Erstelle eine neue Malebene, um weiter entfernt zu zeichnen.',
        patterns: {
            full: 'Quadrat',
            circle: 'Kreis',
            dots: 'Punkte',
            dots2: 'Punkte (2px)',
            checkerboard: 'Schachbrett',
            checkerboardReverse: 'Schachbrett (umgekehrt)',
            grid: 'Raster',
            horizontal1: 'Horizontale Linien (1px)',
            horizontal2: 'Horizontale Linien (2px)',
            vertical1: 'Vertikale Linien (1px)',
            vertical2: 'Vertikale Linien (2px)',
            diagonal2: 'Diagonal (2px)',
            diagonal2Reverse: 'Diagonal umgekehrt (2px)',
            diagonal3: 'Diagonal (3px)',
            diagonal3Reverse: 'Diagonal umgekehrt (3px)',
        },
    },
    fonts: {
        importFont: 'Schriftart importieren …',
        deleteFont: 'Schriftart löschen',
        fontInUse: 'Eine Schriftart, die im aktuellen Projekt verwendet wird, kann nicht gelöscht werden',
        readFailed: 'Diese Schriftartdatei konnte nicht gelesen werden.',
        wizard: {
            previewTitle: 'Schriftart-Vorschau: {name}',
            previewZoom: 'Vorschau-Zoom',
            fontHeight: 'Schrifthöhe',
            previewText: 'Vorschautext',
            import: 'Importieren',
        },
    },
    screens: {
        addScreen: 'Screen hinzufügen',
        upgradeToAdd: 'Upgrade, um weitere Screens hinzuzufügen',
        fallbackTitle: 'Screen',
        creating: 'Screen wird erstellt',
        deleting: 'Screen wird gelöscht',
        hiddenInProjectSettings: 'In den Projekteinstellungen verborgen',
        actions: {
            publish: 'Veröffentlichen',
            makePublic: 'Öffentlich machen',
            makePrivate: 'Privat machen',
            unlimitedHint: 'Upgrade für unbegrenzte Screens',
            privacyHint: 'Upgrade für private Screens',
        },
        carousel: {
            open: '{project} in Canvas Flow öffnen',
            previewAlt: '{project} – Screen {number}',
            screenTitle: 'Screen {number}',
            previewUnavailable: 'Vorschau nicht verfügbar',
            emptyCanvas: 'Leere Zeichenfläche',
            previous: 'Vorheriger Screen',
            next: 'Nächster Screen',
        },
    },
    flow: {
        layers: 'Ebenen',
        screensLeft: 'Noch {count} kostenloser Screen übrig. | Noch {count} kostenlose Screens übrig.',
        seePlans: 'Tarife mit mehr Screens ansehen',
        canvasLabel: 'Zeichenfläche des Projekts',
        imageSaved: 'Bild erfolgreich gespeichert',
        view: {
            fitAll: 'Alles einpassen',
            fitSelection: 'Auswahl einpassen',
            hideInspector: 'Inspektor ausblenden',
            showInspector: 'Inspektor einblenden',
        },
        liveView: {
            connect: 'Live-Ansicht',
            disconnect: 'Trennen',
            hint: 'Sieh dir dein Design per USB in Echtzeit auf deinem Flipper an',
            noDevice: 'Kein Flipper gefunden. Verbinde deinen Flipper per USB und versuche es erneut.',
            disconnected:
                'Die Live-Ansicht wurde getrennt. Verbinde deinen Flipper erneut per USB und versuche es noch einmal.',
        },
        code: {
            tabs: 'Screen-Code',
            copyHint: 'Quellcode kopieren',
            empty: 'Erstelle einen Screen, um Code zu generieren.',
            importHint: 'Code importieren (experimentell): funktioniert am besten mit in Lopaka generiertem Code',
            loadFailed: 'Der Screen-Code konnte nicht geladen werden.',
            generateFailed: 'Für diesen Screen konnte kein Code generiert werden.',
            importFailed: 'Der Code konnte nicht importiert werden.',
            copyFailed: 'Der Code konnte nicht in die Zwischenablage kopiert werden.',
        },
        animation: {
            title: 'Animation',
            discardChanges: 'Ungespeicherte Änderungen an der Animation verwerfen?',
        },
        errors: {
            openFailed: 'Das Projekt konnte nicht geöffnet werden.',
            screenAddFailed: 'Der Screen konnte nicht hinzugefügt werden. Bitte versuche es erneut.',
            screenUpdateFailed: 'Der Screen konnte nicht aktualisiert werden.',
            imageLoadFailed: 'Ein Bild des Projekts konnte nicht geladen werden.',
            assetsLoadFailed: 'Einige Assets des Projekts konnten nicht geladen werden.',
            unsavedKeepOpen: 'Deine letzten Änderungen wurden nicht gespeichert. Lass diesen Tab geöffnet.',
            unsaved: 'Deine letzten Änderungen wurden nicht gespeichert.',
            previewFailed: 'Die Screen-Vorschau konnte nicht aktualisiert werden.',
            imageUploadFailed: 'Das Bild {name} konnte nicht hochgeladen werden.',
            imagesImportFailed: 'Die Bilder konnten nicht importiert werden.',
            assetAddFailed: 'Das Asset konnte nicht hinzugefügt werden.',
            imageSaveFailed: 'Das Bild konnte nicht gespeichert werden.',
            animationOpenFailed: 'Die Animation konnte nicht geöffnet werden.',
            animationSaveFailed: 'Die Animation konnte nicht gespeichert werden.',
            documentUpdateFailed: 'Die Änderung konnte nicht auf die Zeichenfläche übernommen werden.',
        },
    },
    platforms: {
        picker: {
            placeholder: 'Plattform wählen',
            setup: 'Einrichtung: {hint}',
            memory: 'Speicher: {hint}',
            noDescription: 'Keine Beschreibung verfügbar.',
            github: 'Auf GitHub ansehen',
            mono: 'Mono',
            easy: 'Einfach',
            hard: 'Schwer',
        },
        details: {
            tftEspi: {
                description:
                    'Eine Zeichen-API für die farbigen SPI-Panels – ILI9341, ST7789, GC9A01 usw. Treiber, Pins und SPI-Takt stehen in der bibliothekseigenen User_Setup.h, die Bibliothek wird also vor dem Kompilieren deines Sketches konfiguriert, nicht aus deinem Code. M5GFX und LovyanGFX bieten dieselben Aufrufe. Keine Widgets und kein automatisches Neuzeichnen: Du zeichnest, wenn du es entscheidest. Der Code wird als Arduino C++ oder ESP-IDF C++ exportiert.',
                sizeHint: 'Benötigt ESP32, ESP8266, RP2040 oder STM32',
                setupHint: 'Treiber und Pins in User_Setup.h festlegen',
            },
            u8g2: {
                description:
                    'Die Standardwahl für monochrome OLED- und LCD-Panels wie SSD1306, SH1106 und ST7565, bei denen jedes Pixel einfach an oder aus ist. Die Schriftartenliste umfasst Hunderte BDF-Varianten, meist der Grund für diese Wahl, und die Schriftgröße kommt aus der Schriftdatei statt aus einem Skalierungsfaktor. Der Code wird als Arduino C++ oder ESP-IDF C exportiert.',
                sizeHint: 'Mit Page-Buffer läuft es auf einem Arduino Uno',
                setupHint: 'Eine Konstruktorzeile, keine Konfigurationsdatei',
                monoHint: 'Nur monochrom: ein Bit pro Pixel, keine Farbe',
            },
            adafruitGfx: {
                description:
                    'Die gemeinsame Zeichenschicht unter den meisten Adafruit-Displaytreibern, deshalb gibt es überall Beispiele und Tutorials dafür. Du kombinierst sie mit dem Treiber für dein genaues Panel, etwa Adafruit_ST7789 oder Adafruit_ILI9341, und dieser Treiber kümmert sich um Pins und Initialisierung. Farben sind als RGB565 gepackt, Text kommt aus den GFX-Font-Headern. Gezeichnet wird direkt auf das Panel ohne Framebuffer, sodass selbst 8-Bit-Boards ein Farbdisplay ansteuern können. Der Code wird als Arduino C++ oder ESP-IDF C++ exportiert.',
                sizeHint: 'Kein Framebuffer; passt auf 8-Bit-Boards',
            },
            adafruitGfxMono: {
                description:
                    'Dieselben GFX-Aufrufe und Schriftarten für ein 1-Bit-Display, angesteuert von Adafruit_SSD1306 oder Adafruit_SH110X. Der Treiber hält das ganze Bild in einem RAM-Puffer und überträgt es bei display(), was auf einem 128x64-Panel etwa 1 KB kostet. Für eine weit größere Schriftartensammlung auf derselben Hardware nimm stattdessen U8g2. Der Code wird als Arduino C++ oder ESP-IDF C++ exportiert.',
                sizeHint: 'Passt auf 8-Bit-Boards wie den Arduino Uno',
                setupHint: 'Treiber installieren, Beispiel ausführen',
                monoHint: 'Nur monochrom: ein Bit pro Pixel, keine Farbe',
            },
            lvgl: {
                description:
                    'Eher ein GUI-Toolkit als eine Zeichenbibliothek: Buttons, Slider, Schalter, Checkboxen und Textfelder sind echte Objekte mit eigenen Styles und eigener Ereignisbehandlung. Zum Portieren brauchst du eine lv_conf.h und einen Flush-Callback, der die gerenderten Pixel an deinen Displaytreiber übergibt – rechne also mit Einrichtungsaufwand, bevor das erste Pixel erscheint. Es braucht einen 32-Bit-Mikrocontroller mit freiem RAM.',
                sizeHint: 'Braucht ein 32-Bit-Board mit freiem RAM',
                setupHint: 'Braucht lv_conf.h und einen Flush-Callback',
            },
            arduinoGfx: {
                description:
                    'Eine neuere Variante der GFX-API, bei der das Display-Objekt im Sketch aus einem Datenbus und einer Panel-Klasse zusammengesetzt wird, statt eine Konfigurationsdatei der Bibliothek zu bearbeiten. Neben den gemeinsamen Grundfunktionen bietet sie eigene Bitmap-Funktionen, darunter eine Big-Endian-Variante für Byteströme, die mit dem höherwertigen Byte zuerst ankommen. Gedacht für schnellere 32-Bit-Boards, auch für Parallel- und RGB-Panels am ESP32-S3. Der Code wird als Arduino C++ oder ESP-IDF C++ exportiert.',
                sizeHint: 'Für 32-Bit-Boards wie den ESP32 optimiert',
            },
            esphome: {
                description:
                    'Firmware für ESP32 und ESP8266, beschrieben in einer einzigen YAML-Datei: ESPHome baut und flasht sie für dich, und das Gerät erscheint in Home Assistant. Gezeichnet wird im Lambda der Display-Komponente, während Schriftarten und Bilder eigene YAML-Schlüssel auf oberster Ebene sind und nicht Teil dieses Lambdas. Schriftarten lassen sich per URL direkt von Google Fonts laden. Bilder werden nur als 1-Bit-XBMP exportiert.',
                sizeHint: 'Komplette Firmware; braucht ESP32 oder ESP8266',
                setupHint: 'Nur YAML; ESPHome kompiliert und flasht',
            },
            micropython: {
                description:
                    'Python läuft direkt auf dem Board: Kopiere eine .py-Datei hinüber und sie wird ausgeführt, ohne Toolchain und ohne Kompilierschritt. Gezeichnet wird mit dem eingebauten framebuf-Modul über den MicroPython-SSD1306-Treiber von PerfecXX, die eine Datei, die du auf RP2040, ESP32 und ähnliche Boards kopierst. Nur 1 Bit, mit einer einzigen festen 8x8-Schrift und ohne abgerundete Ecken – das passt eher zu Messwertanzeigen und einfachen Menüs als zu detaillierten Layouts. Dreiecke gibt es hier nur als Umriss.',
                sizeHint: 'Braucht ein MicroPython-Board, RP2040 oder ESP32',
                setupHint: 'Eine .py-Datei aufs Board kopieren',
                monoHint: 'Nur monochrom: ein Bit pro Pixel, keine Farbe',
            },
            circuitpython: {
                description:
                    'Die Python-Variante von Adafruit: Das Board wird als USB-Laufwerk eingebunden, und dein Code läuft neu, sobald du die Datei speicherst. Grafik läuft über displayio und vectorio, die Gruppen von Display-Objekten zusammensetzen und laufend aktualisieren, statt dass du in jedem Frame Pixel überträgst. Farben sind 24-Bit-Hexwerte, Text nutzt die mitgelieferte Terminus-BDF-Schrift, und Bilder werden als rohe Byte-Puffer ausgeliefert. Du brauchst ein Board mit einem CircuitPython-Build, der dein Display unterstützt.',
                sizeHint: 'Braucht ein CircuitPython-Board mit displayio',
                setupHint: 'Board wird als USB-Laufwerk eingebunden',
            },
            flipper: {
                description:
                    'Zeichnet über die Canvas-API der Firmware auf das Display von Flipper Zero und Flipper One, das Ergebnis gehört also in eine Flipper-App, die mit dem App-SDK der Firmware gebaut wird. Nur vier feste Schriftarten.',
                monoHint: 'Nur monochrom: ein Bit pro Pixel, keine Farbe',
                setupHint: 'Wird mit dem Flipper-App-SDK gebaut',
            },
            gxepd2: {
                description:
                    'Für E-Paper-Panels. Sie baut auf Adafruit GFX auf, Zeichenaufrufe und Schriftarten sind also die bekannten, aber du musst die genaue Panel-Klasse aus einer langen Liste angeben, bei der die Modellnummer auf dem Flachbandkabel zählt. Farbe ist ein fester Satz aus neun Konstanten wie GxEPD_BLACK und GxEPD_RED, alles andere wird zu Schwarz.',
                sizeHint: 'Panel-Puffer sind groß; Paging hilft',
                setupHint: 'Genaue Panel-Klasse wählen',
            },
            freestyle: {
                description:
                    'Eine einfache Zeichenfläche ohne Zielbibliothek: volle RGB-Farbe, alle Schriftarten von Lopaka und überhaupt kein generierter Code. Nutze sie, um ein Layout zu skizzieren oder einen Screen zu entwerfen, bevor du dich auf Hardware festlegst, oder einfach, um ein Bild zu exportieren.',
            },
        },
    },
    account: {
        menu: {
            assets: 'Assets',
            plusAccessDaysLeft: 'Plus-Zugang: noch {count} Tag | Plus-Zugang: noch {count} Tage',
            subscriptionDaysLeft: 'Abonnement: noch {count} Tag | Abonnement: noch {count} Tage',
        },
        referral: {
            invite: 'Freunde einladen',
            getFreePlus: 'Plus gratis erhalten',
        },
        redirecting: 'Weiterleitung …',
        cancelFlow: {
            adBlocked:
                'Dein Werbeblocker verhindert die Kündigung des Abonnements. Deaktiviere ihn für lopaka.app und versuche es erneut.',
            paddleUnavailable:
                'Der Kündigungsablauf von Paddle konnte nicht geöffnet werden. Versuche es erneut oder kontaktiere den Support.',
            unavailable: 'Die Kündigung des Abonnements konnte nicht geöffnet werden. Bitte kontaktiere den Support.',
        },
        success: {
            heading: 'Deine Zahlung wird verarbeitet',
            wait: 'Bitte warte, während wir dein Abonnement prüfen.',
            moments: 'Das dauert normalerweise nur einen Moment.',
            checking: 'Abonnementstatus wird geprüft …',
            progress: '{percent} abgeschlossen',
            help: 'Schreib an {email}, wenn weiterhin Probleme auftreten',
        },
    },
    subscription: {
        manage: 'Abonnement verwalten',
        cancel: 'Abonnement kündigen',
        cycles: {
            weekly: 'Wöchentlich',
            monthly: 'Monatlich',
            yearly: 'Jährlich',
            lifetime: 'Lebenslang',
        },
        upgradeModal: {
            title: 'Abonnement upgraden',
            intro: 'Du bist dabei, ein Upgrade von {current} auf {target} anzufragen.',
            currentPlan: 'Aktueller Tarif:',
            requestedPlan: 'Angefragter Tarif:',
            requestedCycle: 'Gewünschter Abrechnungszeitraum:',
            renewalDate: 'Verlängerungsdatum des aktuellen Abonnements:',
            nextRenewal: 'Nächstes Verlängerungsdatum:',
            newPrice: 'Neuer Preis nach dem Upgrade:',
            manualNote:
                'Upgrades werden derzeit manuell bearbeitet. Nach deiner Bestätigung bearbeiten wir es so schnell wie möglich. Bei Problemen schreib an {email}.',
            sent: 'Deine Upgrade-Anfrage wurde gesendet. Wir melden uns in Kürze.',
            failed: 'Die Upgrade-Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.',
            confirm: 'Upgrade bestätigen',
            unknownPlan: 'Unbekannt',
            dateUnavailable: 'Datum nicht verfügbar',
        },
    },
    upgrade: {
        eyebrow: 'Abonnementwechsel',
        headingYearly: 'Zu {plan} jährlich wechseln',
        headingMonthly: 'Zu {plan} monatlich wechseln',
        intro: 'Prüfe die Abrechnungsvorschau von Paddle, bevor du den Abonnementwechsel bestätigst.',
        loading: 'Vorschau des Abonnementwechsels wird vorbereitet …',
        renewalYearly: 'Jährliche Verlängerung',
        renewalMonthly: 'Monatliche Verlängerung',
        credit: 'Guthaben',
        payAtRenewal: 'Bei Verlängerung zahlen',
        payToday: 'Heute zahlen',
        offerApplied: 'Das Angebot {code} wird auf diesen Wechsel angewendet.',
        reactivation:
            'Dein Abonnement ist gekündigt, aber der bezahlte Zugang bleibt bis zum {date} aktiv. Wenn du diesen Wechsel bestätigst, wird die Abrechnung reaktiviert und das anteilige Upgrade sofort angewendet.',
        reactivationPeriodEnd:
            'Dein Abonnement ist gekündigt, aber der bezahlte Zugang bleibt bis zum Ende deines bezahlten Zeitraums aktiv. Wenn du diesen Wechsel bestätigst, wird die Abrechnung reaktiviert und das anteilige Upgrade sofort angewendet.',
        scheduledNote:
            'Dieses Downgrade ist für deinen nächsten Abrechnungszeitraum geplant, dein aktueller Zugang bleibt also bis zur Verlängerung aktiv.',
        prorationNote:
            'Paddle berechnet den endgültigen anteiligen Betrag anhand deines aktuellen Abrechnungszeitraums, der Steuern, der Währung und eventueller Angebote.',
        confirm: 'Wechsel bestätigen',
        changed: 'Dein Abonnement wurde geändert.',
        scheduled: 'Dein Abonnementwechsel ist für den nächsten Abrechnungszeitraum geplant.',
        backToProjects: 'Zurück zu den Projekten',
        contactSupport: 'Support kontaktieren',
        errorFallback: 'Dein Abonnement konnte nicht geändert werden. Schreib an {email}',
        previewFailed: 'Die Vorschau des Abonnementwechsels konnte nicht erstellt werden.',
        changeFailed: 'Dein Abonnement konnte nicht geändert werden.',
        cannotChangeAutomatically: 'Dieses Abonnement kann nicht automatisch geändert werden.',
        continueWithBasic: 'Mit dem Basic-Tarif fortfahren →',
        eligibility: {
            noActiveAnnual: 'Es gibt kein aktives Abonnement für den Wechsel zur jährlichen Abrechnung.',
            noActive: 'Für diesen Wechsel ist kein aktives Abonnement vorhanden.',
            pastDueAnnual: 'Bitte aktualisiere deine Zahlungsmethode, bevor du zur jährlichen Abrechnung wechselst.',
            pastDue: 'Bitte aktualisiere deine Zahlungsmethode, bevor du dein Abonnement änderst.',
            legacyAnnual: 'Dieses Abonnement kann nicht automatisch upgegradet werden. Bitte kontaktiere den Support.',
            manualSupport: 'Dieses Abonnement kann nicht automatisch geändert werden. Bitte kontaktiere den Support.',
            planMismatch: 'Dieser Upgrade-Link passt nicht zum Tarif deines aktuellen Abonnements.',
            alreadyAnnual: 'Du hast bereits die jährliche Abrechnung.',
            unknownCycle:
                'Wir konnten deinen aktuellen Abrechnungszeitraum nicht bestätigen. Bitte kontaktiere den Support.',
            eligibleAnnual: 'Dein Abonnement kann auf jährliche Abrechnung umgestellt werden.',
            weeklyManual: 'Änderungen am Wochenzugang werden manuell bearbeitet. Bitte kontaktiere den Support.',
            lifetimeManual:
                'Der lebenslange Zugang hat keine wiederkehrende Abrechnung. Bitte kontaktiere den Support.',
            yearlyToMonthly:
                'Der Wechsel von jährlicher zu monatlicher Abrechnung wird manuell bearbeitet. Bitte kontaktiere den Support.',
            alreadyCurrent: 'Du hast dieses Abonnement bereits.',
            downgradeManual:
                'Downgrades werden manuell bearbeitet, damit dein aktueller Zugang bis zur Verlängerung aktiv bleibt.',
            eligible: 'Dein Abonnement kann sofort geändert werden.',
        },
    },
    cancellation: {
        intro: 'Bitte beantworte eine kurze Umfrage',
        redirecting: 'Du wirst zum Portal für die Abonnementverwaltung weitergeleitet …',
        step: 'Schritt {current} von {total}',
        change: 'Ändern',
        otherDetails: 'Kannst du uns sagen, was genau?',
        otherPlaceholder: 'Erzähl uns mehr …',
        commentsTitle: 'Gibt es noch etwas, das wir wissen sollten?',
        commentsHint: 'Dein Feedback hilft uns, Lopaka für alle besser zu machen.',
        commentsPlaceholder: 'Details zu deiner Erfahrung, fehlenden Funktionen oder sonst etwas …',
        questions: {
            mainReason: 'Was ist der Hauptgrund für die Kündigung?',
            contributingFactors: 'Hat noch etwas anderes dazu beigetragen?',
            experience: 'Wie bewertest du deine Erfahrung insgesamt?',
            future: 'Was könnte dich überzeugen zu bleiben?',
        },
        options: {
            notEnoughValue: 'Ich sehe im kostenpflichtigen Tarif nicht genug Mehrwert',
            freeTierEnough: 'Der kostenlose Tarif reicht mir',
            switchedTool: 'Ich bin zu einem anderen Tool gewechselt',
            other: 'Sonstiges',
            nothingElse: 'Nein, das ist alles',
            lowerPrice: 'Ein niedrigerer Preis',
            betterOnboarding: 'Eine bessere Einführung',
            moreExport: 'Mehr Exportoptionen',
            alreadyDecided: 'Nichts, ich habe mich schon entschieden',
        },
    },
    referrals: {
        closeDialog: 'Einladungsdialog schließen',
        invite: {
            heading: 'Lade Freunde ein',
            text: 'Erhalte eine Woche Plus kostenlos, sobald dein Freund zum ersten Mal etwas kauft.',
            ticketAlt: 'Sieben Tage Lopaka Plus für jeden zahlenden Freund',
            friendBenefit: 'Dein Freund erhält {extraDays} in seiner ersten Plus-Woche {lineBreak}oder {discount}.',
            extraDays: '7 zusätzliche Tage',
            discount: '10 % Rabatt auf Lifetime',
            linkLabel: 'Dein Einladungslink',
            copyLink: 'Einladungslink kopieren',
            linkCopied: 'Einladungslink kopiert',
            paidReferrals: 'Bezahlte Empfehlungen',
            bonusDays: 'Verdiente Bonustage',
        },
        share: {
            heading: 'Teile deine Arbeit',
            text: 'Erhalte eine Woche Plus kostenlos für jeden freigegebenen Beitrag über Lopaka.',
            ticketAlt: 'Sieben Tage Lopaka Plus – eine Woche pro Beitrag',
            ideas: 'Zeig dein Projekt, schreib ein Tutorial oder einen Artikel oder teile einen Beitrag oder ein Video. Erwähne nach Möglichkeit {brand} oder verlinke auf {site}.',
            linkLabel: 'Link zu deinem Beitrag',
            submit: 'Zur Prüfung einreichen',
            submitted: 'Eingereicht! Wir schicken dir eine E-Mail, sobald er freigegeben ist.',
            alreadySubmitted: 'Dieser Beitrag wurde bereits eingereicht.',
            failed: 'Dein Beitrag konnte nicht eingereicht werden. Bitte versuche es erneut.',
            reviewNote:
                'Wir prüfen jeden Link manuell und schicken dir eine E-Mail, sobald er freigegeben ist. Du hast mehr zu teilen? Schick für jeden Beitrag einen eigenen Link.',
        },
    },
    editor: {
        screenNotFound: '404 – Screen nicht gefunden',
        uploadingImages: 'Bilder werden hochgeladen. Bitte warten…',
        animationTitle: 'Animation',
        grid: 'Raster',
        zoomIn: 'Vergrößern',
        zoomOut: 'Verkleinern',
        imageTooLarge: 'Das Bild hat {width}×{height} Pixel; Bilder dürfen höchstens {max} Pixel haben.',
        layerDataMissing: 'Die Daten der Bildebene fehlen. Sie wird nicht gezeichnet.',
        loadingProject: 'Projekt wird geladen',
        flipper: {
            liveView: 'Live-Ansicht',
            disconnect: 'Trennen',
            liveViewHint: 'Zeige dein Design in Echtzeit per USB auf deinem Flipper an',
            notFound: 'Kein Flipper gefunden. Schließe deinen Flipper per USB an und versuche es erneut.',
        },
        animation: {
            preparing: 'Animationsframes werden vorbereitet',
            preparingProgress: 'Animationsframes werden vorbereitet ({completed}/{total})',
            discardChanges: 'Ungespeicherte Änderungen an der Animation verwerfen?',
            editingUnavailable:
                'Animationsframes kannst du nur in einem gespeicherten Projekt bearbeiten, das du bearbeiten darfst.',
            prepareFailed: 'Die Animation konnte nicht vorbereitet werden.',
            saveFailed: 'Die Änderungen an der Animation konnten nicht gespeichert werden.',
            checkingSave: 'Das Ergebnis des letzten Speicherns wird vor dem Schließen geprüft…',
            saveUnknown: 'Das Ergebnis des letzten Speicherns ist noch unbekannt.',
            createFailed: 'Die Animation konnte nicht erstellt werden.',
            importFailed: 'Die Animation konnte nicht importiert werden.',
            layerLoadFailed: 'Die Animation „{name}“ konnte nicht geladen werden.',
            pastedLoadFailed: 'Die eingefügte Animation „{name}“ konnte nicht geladen werden.',
        },
        assets: {
            imagesAddFailed: 'Die Bilder konnten nicht hinzugefügt werden.',
            imagesNotUploaded:
                '{count} Bild konnte nicht hochgeladen werden. | {count} Bilder konnten nicht hochgeladen werden.',
            imageAddFailed: 'Das Bild konnte nicht hinzugefügt werden.',
            animationAddFailed: 'Die Animation konnte nicht hinzugefügt werden.',
            animationLoadFailed: 'Die Animation konnte nicht geladen werden.',
            notFound: 'Dieses Asset ist im aktuellen Editor nicht verfügbar.',
            droppedImageFailed: 'Das abgelegte Bild „{name}“ konnte nicht geladen werden.',
            droppedImageRejected: 'Das abgelegte Bild „{name}“ konnte nicht geladen werden: {reason}',
        },
        codeImport: {
            noLayers: 'Es wurden keine Ebenendaten gefunden.',
            fontNotFound: 'Die Schriftart {font} wurde nicht gefunden. Die Standardschriftart wird verwendet.',
            imageSkipped: 'Das Bild {name} wurde übersprungen. {reason}',
        },
        sidebar: {
            label: 'Editor-Bereiche',
            layers: 'Ebenen',
            images: 'Bilder',
            animations: 'Animationen',
            fonts: 'Schriftarten',
            components: 'Komponenten',
            prototype: 'Prototyp',
        },
        appNav: {
            projects: 'Projekte',
            assets: 'Assets',
        },
        publish: {
            fork: 'Fork',
            forkHint: 'Erstelle eine Kopie dieses Projekts',
            forkSignInHint: 'Melde dich an, um dieses Projekt zu forken',
            share: 'Teilen',
            copyPublicLink: 'Öffentlichen Link kopieren',
            linkCopied: 'Der Link wurde in die Zwischenablage kopiert',
            publishBoth: 'Du musst sowohl das Projekt als auch den Screen veröffentlichen',
            publishProject: 'Veröffentliche es zuerst in den Projekteinstellungen',
            publishScreen: 'Veröffentliche zuerst den Screen',
            presentation: 'Präsentation',
            presentationUpgrade: 'Upgrade auf Plus, um den Präsentationsmodus zu aktivieren',
            saveAs: 'Speichern unter',
            exportUpgrade: 'Upgrade deinen Tarif, um den Dateiexport zu aktivieren',
            textFile: 'Textdatei',
            pngImage: 'PNG-Bild',
            bmpImage: 'BMP-Bild',
            imageSaved: 'Das Bild wurde gespeichert',
            codeSaved: 'Der Quellcode wurde gespeichert',
        },
        presenter: {
            screenImage: 'Bild des Screens',
            thumbnail: 'Vorschaubild des Screens',
            noImage: 'Kein Bild verfügbar',
            fullscreen: 'Vollbild',
            exitFullscreen: 'Vollbild beenden',
        },
    },
    tools: {
        undo: 'Rückgängig',
        redo: 'Wiederholen',
        select: 'Auswählen',
        animation: 'Animation',
        arc: 'Bogen',
        button: 'Button',
        checkbox: 'Checkbox',
        circle: 'Kreis',
        ellipse: 'Ellipse',
        icon: 'Icon',
        image: 'Bild',
        line: 'Linie',
        paint: 'Malen',
        panel: 'Panel',
        polygon: 'Polygon',
        rect: 'Rechteck',
        slider: 'Schieberegler',
        switch: 'Schalter',
        text: 'Text',
        textArea: 'Textbereich',
        triangle: 'Dreieck',
    },
    layers: {
        emptyText: 'Leerer Text',
        unnamed: 'Unbenannte Ebene',
    },
    code: {
        placeholder: 'Schreib deinen Code hier…',
        copySource: 'Quellcode kopieren',
        copied: 'In die Zwischenablage kopiert',
        importHint: 'Code importieren (experimentell): funktioniert am besten mit Code, der in Lopaka erzeugt wurde',
        syntax: 'Syntax',
        fonts: 'Schriftarten',
        images: 'Bilder',
        animations: 'Animationen',
        onlyUsedGlyphs: 'Nur verwendete Glyphen rendern',
        fontConvertFailed:
            '{font} konnte nicht in einen U8g2-Schriftart-Header umgewandelt werden. Es wurde keine Datei heruntergeladen.',
        settings: {
            wrap: 'Wrapper-Funktion',
            progmem: 'Als PROGMEM deklarieren',
            includeFonts: 'Schriftarten einbinden',
            includeImages: 'Bilder deklarieren',
            includeImageFiles: 'Bilddateien einbinden',
            includeAnimations: 'Animations-Hilfsfunktionen',
            declareVars: 'Variablen deklarieren',
            comments: 'Kommentare, Ebenentitel',
            clearScreen: 'Display leeren/füllen',
        },
    },
    shortcuts: {
        title: 'Tastenkürzel',
        move10px: '10px verschieben',
        reorderLayers: 'Ebenen neu anordnen',
        copyPasteCut: 'Kopieren/Einfügen/Ausschneiden',
        selectAll: 'alles auswählen',
        undo: 'rückgängig machen',
        redo: 'wiederholen',
        zoom: 'vergrößern/verkleinern',
        multiSelect: 'Mehrfachauswahl',
        resetSelection: 'Auswahl aufheben',
        groupLayers: 'Ebenen gruppieren',
        ungroup: 'Gruppierung aufheben',
        resizing: 'Größe ändern',
        keepAspectRatio: 'Halte {key} gedrückt, um das Seitenverhältnis beizubehalten',
        resizeFromCenter: 'Halte {key} gedrückt, um die Größe von der Mitte aus zu ändern',
        drawLine: 'Halte {key} gedrückt, um eine Linie zu zeichnen',
        eraser: 'Radierer',
        polygonTriangle: 'Polygon, Dreieck',
        placeVertices: 'Eckpunkte setzen',
        either: '{first} oder {second}',
        finish: 'abschließen',
        editVertices: 'Eckpunkte bearbeiten',
        click: 'Klick',
        rightClick: 'Rechtsklick',
        doubleClick: 'Doppelklick',
    },
    galleryProject: {
        previewNotice:
            'Das ist ein öffentliches Projekt aus der Galerie. Um es weiter zu bearbeiten, übernimm es mit {fork} in deine Projekte.',
        fork: 'FORK',
        loadFailed: 'Dieses Projekt konnte nicht geladen werden. Prüfe deine Verbindung und versuche es erneut.',
        screens: 'Screens:',
        about: {
            heading: {
                design: 'Über dieses Design',
                display: 'Über dieses Display-Design',
                monochrome: 'Über dieses Design für Monochrom-Displays',
                colorTft: 'Über dieses Design für Farb-TFT-Displays',
                ePaper: 'Über dieses Design für E-Paper-Displays',
            },
            stepsHeading: 'So bekommst du den {platform}-Code für diesen Screen',
            stepsHeadingDesign: 'So verwendest du dieses Design',
            summary:
                '{project} ist eine {platform}-Benutzeroberfläche für ein Display mit {size} Pixeln, geteilt von der Lopaka-Community.',
            viewingScreen: 'Du siehst den Screen „{screen}“.',
            screensCount: 'Das Projekt hat {count} Screens; wähle einen in der Screens-Liste aus.',
            platformNote: 'Lopaka erzeugt {platform}-Code für dieses Layout, bereit zum Einfügen in deine Firmware.',
            platformNotes: {
                u8g2: 'U8g2 ist eine Monochrom-Grafikbibliothek für Arduino-, ESP32- und STM32-Boards. Sie steuert verbreitete OLED- und LCD-Controller wie SSD1306, SH1106 und ST7920 und bringt Hunderte kompakte Bitmap-Schriftarten mit.',
                tftEspi:
                    'TFT_eSPI ist eine schnelle Grafikbibliothek für Farb-TFT-Displays auf ESP32-, ESP8266- und RP2040-Boards. Sie unterstützt Controller wie ILI9341, ST7789 und ST7735 und zeichnet in 16-Bit-RGB565-Farbe.',
                adafruitGfx:
                    'Adafruit GFX ist die zentrale Grafikbibliothek hinter den meisten Adafruit-Treibern für Farbdisplays unter Arduino. Dieselben Zeichenaufrufe funktionieren mit ST7735, ST7789, ILI9341 und vielen anderen Farbdisplays.',
                adafruitGfxMono:
                    'Adafruit GFX ist die zentrale Grafikbibliothek hinter den meisten Adafruit-Displaytreibern für Arduino. Im Monochrom-Modus arbeitet sie mit Treibern wie Adafruit_SSD1306 für kleine OLED-Displays zusammen.',
                arduinoGfx:
                    'Arduino GFX ist eine Display-Bibliothek, die viele Farb-Controller und Datenbusse unterstützt, von SPI-TFT-Panels bis zu parallelen und RGB-Displays auf ESP32-S3-Boards.',
                lvgl: 'LVGL ist eine Open-Source-Bibliothek für Embedded-GUIs auf Mikrocontrollern. Sie bietet Widgets, Stile und Animationen für Farbdisplays auf ESP32, STM32 und anderen Boards mit genügend Speicher.',
                esphome:
                    'ESPHome erstellt Firmware für ESP32- und ESP8266-Geräte aus einer YAML-Konfiguration. Seine Display-Komponente rendert Lambdas wie dieses auf OLED-, TFT- und E-Paper-Displays, die mit Home Assistant verbunden sind.',
                micropython:
                    'MicroPython führt Python 3 direkt auf Mikrocontrollern wie dem ESP32 und dem Raspberry Pi Pico aus. Sein Modul framebuf zeichnet Text, Formen und Bitmaps für kleine Displays.',
                circuitpython:
                    'CircuitPython ist ein Fork von MicroPython von Adafruit für Einsteiger. Seine Bibliothek displayio setzt Bitmaps, Labels und Formen für OLED-, TFT- und E-Paper-Displays zusammen.',
                flipper:
                    'Flipper-Zero-Apps zeichnen über die Canvas-API der Flipper-Firmware auf dessen 128×64-Monochrom-Display. Dafür erstellte Designs passen genau auf das Gerät.',
                inkplate:
                    'Inkplate-Boards kombinieren einen ESP32 mit einem recycelten E-Paper-Display. Die Inkplate-Arduino-Bibliothek zeichnet Text, Formen und Bitmaps in Schwarzweiß oder Graustufen.',
                uint32: 'Die Rohausgabe uint32 exportiert das Design als einfache Pixel-Arrays, bereit für einen eigenen Displaytreiber oder jedes Framework, das rohe Bitmaps akzeptiert.',
                freestyle:
                    'Projekte mit freiem Zeichnen sind Pixel-Art ohne Zielbibliothek. Forke eines, um seine Ebenen in einem Projekt für dein eigenes Display wiederzuverwenden.',
                gxepd2: 'GxEPD2 ist eine Arduino-Bibliothek für SPI-E-Paper-Displays von Waveshare, Good Display und anderen. Sie übernimmt Teil- und Vollaktualisierungen für stromsparende Displays, die ihr Bild auch ohne Strom behalten.',
            },
            codeNote:
                'Jede Ebene wird im exportierten {platform}-Code zu einem eigenen Zeichenaufruf: Text behält seine Schriftart, Icons und Bilder werden zu Bitmap-Arrays, und Formen werden zu Linien, Rechtecken und Kreisen. So bleibt der Code lesbar und lässt sich leicht von Hand ändern.',
            codeNoteDesign:
                'Jede Ebene bleibt nach dem Forken bearbeitbar: Text behält seine Schriftart, Icons und Bilder bleiben einzelne Bitmaps, und Formen bleiben Formen, die du verschieben und skalieren kannst.',
            steps: {
                copyCode:
                    'Kopiere den generierten {platform}-Code aus dem Code-Bereich und füge ihn in deinen Sketch oder deine Firmware ein.',
                codeSettings:
                    'Passe die Code-Einstellungen neben dem Bereich an, etwa wie Bilder und Schriftarten exportiert werden.',
                star: 'Gib dem Projekt einen Stern, um es im Tab „Favoriten“ der Galerie zu behalten.',
                fork: 'Forke das Projekt, um eine eigene Kopie unter „Meine Projekte“ zu speichern. Zum Forken brauchst du ein kostenloses Lopaka-Konto.',
                edit: 'Bearbeite Text, Formen, Icons und Bilder in deiner Kopie – der Code aktualisiert sich, während du arbeitest.',
                canvas: 'Behalte die Zeichenfläche mit {size} für dasselbe Display oder passe ihre Größe an dein Display an.',
                publish: 'Veröffentliche deine Version in der Galerie, um sie mit der Community zu teilen.',
            },
            guide: 'Lies die Anleitung zu {name}',
            platformsGuide: 'Lies die Anleitung zu den Plattformen',
        },
    },
    imageWizard: {
        processing: {
            animationFrames: 'Animations-Frames werden verarbeitet …',
            animationFramesProgress: 'Animations-Frames werden verarbeitet ({completed}/{total}) …',
            imageFrames: 'Bild-Frames werden verarbeitet …',
            imageFramesProgress: 'Bild-Frames werden verarbeitet ({completed}/{total}) …',
            images: 'Bilder werden verarbeitet und als Ebenen zur Zeichenfläche hinzugefügt …',
        },
        sourceType: {
            uploadFile: 'Datei hochladen',
            byteArray: 'Byte-Array eingeben',
        },
        upload: {
            dropHint: 'Wähle Dateien aus oder zieh sie hierher',
            browse: 'Dateien durchsuchen',
            supportedTypes: 'Unterstützte Dateitypen',
            categories: {
                images: 'Bilder',
                gifs: 'GIFs',
                videos: 'Videos',
            },
        },
        bitmap: {
            placeholder: 'Füge das Byte-Array hier ein. Beispiel: 0x0A, 0x0B, 0x09',
            width: 'Breite:',
            height: 'Höhe:',
            swapBytes: 'Bytes tauschen',
        },
        import: 'Importieren',
        images: 'Bilder',
        imagesSelected: 'Bilder ({count})',
        preview: {
            videoTitle: 'Videovorschau',
            videoFrameAt: 'Videovorschau bei {time}',
            gifTitle: 'Animations-Zeitleiste',
            gifFrameAt: 'GIF-Frame bei {time}',
        },
        metrics: {
            fps: 'Frames pro Sekunde',
            duration: 'Dauer',
            frames: 'Frames',
            frameSize: 'Frame-Größe',
            estimatedSize: 'Geschätzte Größe',
            sourceFrames: 'Quell-Frames',
            outputFrames: 'Ausgabe-Frames',
        },
        warnings: {
            fpsRange: 'Frames pro Sekunde müssen eine ganze Zahl von 1 bis 60 sein.',
            videoFrameLimit:
                'Diese Einstellung erzeugt {count} Frames. Verringere die FPS, um das Limit von {limit} Frames einzuhalten.',
            gifFrameLimit:
                'Diese Einstellung erzeugt mehr als {limit} Frames. Verringere die FPS, um das Frame-Limit einzuhalten.',
            sizeLimit:
                'Die geschätzte Animation überschreitet das Größenlimit von {limit}. Verringere die FPS oder die Abmessungen der verarbeiteten Frames.',
        },
        mode: {
            label: 'Importmodus für animierte Dateien',
            frames: 'Frames',
            animation: 'Animation',
            toggle: 'Als Animation importieren',
        },
        editor: {
            name: 'Name:',
            zoom: 'Zoom:',
            monochrome: 'Monochrom',
            rgb: 'RGB',
            byteSize: '{size} Byte',
            dimensions: 'Abmessungen',
            widthShort: 'B',
            heightShort: 'H',
            fitScreen: 'An Display anpassen',
            cropArea: 'Zuschneidebereich',
            brightness: 'Helligkeit',
            contrast: 'Kontrast',
            grayscaleFirst: 'Zuerst Graustufen',
            invertPalette: 'Palette invertieren',
            invertResult: 'Ergebnis invertieren',
            resampling: {
                label: 'Resampling',
                highQuality: 'Hohe Qualität',
                bilinear: 'Bilinear',
                nearest: 'Nächster Nachbar',
            },
            dithering: {
                label: 'Dithering',
                floydSteinberg: 'Floyd-Steinberg',
                atkinson: 'Atkinson',
                bayer: 'Bayer 8x8',
                none: 'Kein Dithering',
            },
        },
        errors: {
            loadFailed: 'Die Bilder konnten nicht geladen werden. Prüfe die Dateien und versuche es erneut.',
            processFailed: 'Die Bilder konnten nicht verarbeitet werden. Versuche es erneut.',
            videoProcessFailed:
                'Das Video konnte nicht verarbeitet werden. Versuche es erneut oder wähle ein anderes Video.',
            removeFailed:
                'Das Bild konnte nicht aus dem Projektspeicher entfernt werden. Lade die Seite neu, um es erneut zu versuchen.',
        },
    },
    icons: {
        currentProject: 'Aktuelles Projekt',
        upgradeBanner: 'Upgrade deinen Tarif, um vollen Zugriff auf alle Icon-Pakete zu erhalten',
        upgradeHint: 'Upgrade für vollen Zugriff auf alle Icons',
        noCustomImages: 'Dieses Projekt hat keine eigenen Bilder oder Icons',
        searchLabel: 'Icons nach Namen suchen',
        clearSearch: 'Icon-Suche zurücksetzen',
        noResults: 'Keine Icons gefunden',
        seeMore: 'Mehr anzeigen →',
    },
    animations: {
        categories: {
            ui: 'UI',
            web: 'Web',
            social: 'Social',
            media: 'Medien',
            weather: 'Wetter',
            ecommerce: 'E-Commerce',
            loaders: 'Loader',
            emoji: 'Emoji',
            gestures: 'Gesten',
            food: 'Essen',
            sports: 'Sport',
            custom: 'Eigene',
        },
        detail: {
            high: 'Hoch',
            medium: 'Mittel',
            low: 'Niedrig',
        },
        frameCount: '{count} Frame | {count} Frames',
        upgradeTooltip: 'Upgrade für vollen Zugriff',
        deleteCustomWarning:
            'Diese eigene Animation löschen? Alle Ebenen im aktuellen Projekt, die diese Animation verwenden, sind dann nicht mehr verfügbar.',
        searchLabel: 'Animationen nach Namen suchen',
        clearSearch: 'Suche zurücksetzen',
        createHint: 'Neue eigene Animation hinzufügen',
        createNew: 'Neu erstellen',
        noResults: 'Keine Animationen gefunden',
        custom: {
            loading: 'Gespeicherte Animationen werden geladen …',
            loadFailed:
                'Gespeicherte Animationen sind nicht verfügbar. Wähle erneut Eigene, um es nochmal zu versuchen.',
            empty: 'Keine gespeicherten Animationen',
            use: 'Gespeicherte Animation {id} verwenden',
            unavailableLabel: 'Gespeicherte Animation nicht verfügbar',
            unavailable: 'Nicht verfügbar',
        },
    },
    assets: {
        title: 'Assets',
        stats: {
            total: 'Gesamt',
            images: 'Bilder',
            fonts: 'Schriftarten',
            remaining: 'Verbleibend',
            unlimited: 'Unbegrenzt',
        },
        upgrade: 'Upgrade, um mehr hinzuzufügen',
        empty: 'Noch keine Assets hochgeladen.',
        emptyHint: 'Lade Bilder und Schriftarten im Editor hoch, um sie hier zu sehen.',
        count: '{count} Asset | {count} Assets',
        delete: '{name} löschen',
        untitledProject: 'Projekt {id}',
    },
    docsSite: {
        openApp: 'Lopaka-App öffnen',
        sidebar: {
            documentation: 'Dokumentation',
            overview: 'Überblick',
            gettingStarted: 'Erste Schritte',
            platforms: 'Plattformen',
            editorOverview: 'Editor im Überblick',
            importingImages: 'Bilder importieren',
            animations: 'Animationen',
            fonts: 'Schriftarten',
            codeGeneration: 'Code-Generierung',
            keyboardShortcuts: 'Tastenkürzel',
            subscription: 'Abonnement',
            plansDescription: 'Tarife im Überblick',
            platformGuides: 'Plattform-Anleitungen',
        },
        outlineLabel: 'Auf dieser Seite',
        previousPage: 'Vorherige Seite',
        nextPage: 'Nächste Seite',
        langMenuLabel: 'Sprache ändern',
        sidebarMenuLabel: 'Menü',
        returnToTopLabel: 'Nach oben',
        darkModeSwitchLabel: 'Darstellung',
        lightModeSwitchTitle: 'Zum hellen Design wechseln',
        darkModeSwitchTitle: 'Zum dunklen Design wechseln',
        skipToContentLabel: 'Zum Inhalt springen',
        notFound: {
            title: 'SEITE NICHT GEFUNDEN',
            quote: 'Aber wenn du deine Richtung nicht änderst und weitersuchst, kommst du vielleicht dort an, wohin du unterwegs bist.',
            linkLabel: 'zur Startseite',
            linkText: 'Zur Startseite',
        },
        search: {
            buttonText: 'Suchen',
            displayDetails: 'Detaillierte Liste anzeigen',
            resetButtonTitle: 'Suche zurücksetzen',
            backButtonTitle: 'Suche schließen',
            noResultsText: 'Keine Ergebnisse für',
            selectText: 'zum Auswählen',
            selectKeyAriaLabel: 'Eingabetaste',
            navigateText: 'zum Navigieren',
            navigateUpKeyAriaLabel: 'Pfeil nach oben',
            navigateDownKeyAriaLabel: 'Pfeil nach unten',
            closeText: 'zum Schließen',
            closeKeyAriaLabel: 'Escape',
        },
    },
};

export default de;
